import { Badge, Flex, Tooltip, Typography } from "antd"
import dayjs from "dayjs"

import InfoTooltip from "../../../Components/InfoTooltip/InfoTooltip"
import Private from "../../../Components/Private"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
// Status badge colour, label, description and the filter option list come from the
// shared single source, so the Activity stream renders and filters statuses
// identically to the /recover/all table (no second copy to drift, and 'Under review'
// is correctly excluded from the filter). Re-exported because the activity full page
// imports STATUS_DATA from this module.
import {
  STATUS_DATA,
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  STATUS_TAG_COLORS
} from "../../DisputesPage/pages/AllPage/constants/statusMeta"
import { ACTIVITY_LABELS } from "../utils/activityLabels"
import { DISPUTE_EVENT_FILTER_DATA, EVENT_DETAILS } from "../utils/disputeEvents"

const { Text, Link } = Typography

// The "What changed" filter options: one per event type, in the order the events happen.
export const ACTIVITY_CHANGE_DATA = DISPUTE_EVENT_FILTER_DATA

export { STATUS_TAG_COLORS, STATUS_LABELS, STATUS_DATA }

// The categories activity/ filters by (DisputeCategory), worded as the list's Category filter.
export const REASON_DATA = [
  { id: "fraud", name: "Fraud" },
  { id: "consumer_disputes", name: "Consumer Disputes" },
  { id: "processing_errors", name: "Processing Errors" },
  { id: "authorization_related_chargeback", name: "Authorization" },
  { id: "unknown", name: "Other" }
]

// Deep-link the customer name to the disputed transaction, mirroring /recover/all.
const transactionUrl = (row) =>
  row?.processor === "stripe" && row?.provider_charge_id
    ? `https://dashboard.stripe.com/payments/${row.provider_charge_id}`
    : null

// The When column. Relative inside the last 24 hours ("just now", "5min ago", "2h ago"),
// where "how long ago" is what you are actually asking; beyond that the exact date and
// time, because "35d ago" tells you nothing without doing the arithmetic yourself (and a
// hover is not an answer). Interpolated through t() so the units can be localised.
const EXACT_FORMAT = "DD MMM YYYY, HH:mm"

// Exact timestamps are rendered and labelled in UTC, the same as the dispute's own activity
// log, so the same event reads identically wherever it is shown and nobody has to guess whose
// clock a date belongs to. Relative times need no label: an interval is the same in any zone.
const exactTime = (iso) => `${dayjs.utc(iso).format(EXACT_FORMAT)}\u00a0(UTC)`

const eventTimeText = (iso, t) => {
  const then = dayjs(iso)
  if (!then.isValid()) return ""
  const sec = Math.max(0, dayjs().diff(then, "second"))
  if (sec < 60) return t("just now")
  const min = Math.floor(sec / 60)
  if (min < 60) return t("_count_min ago", { count: min })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t("_count_h ago", { count: hr })
  return exactTime(iso)
}

// Relative times keep the exact timestamp on hover; an exact time needs no tooltip.
const isRelative = (iso) => {
  const then = dayjs(iso)
  return then.isValid() && dayjs().diff(then, "hour") < 24
}

// The activity-feed columns over activity/'s rows. The dispute columns (#, status, customer,
// amount, category) render exactly like the /recover/all table; "What changed" (the event)
// and "When" are the feed's own.
export const buildColumns = (t) => [
  {
    key: "dispute_id",
    dataIndex: "dispute_id",
    title: <Icon type="hashtag" />,
    width: 72,
    render: (value) => <Text style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
  },
  {
    key: "status",
    dataIndex: "status",
    title: t("Status"),
    width: 210,
    // Status only: the evidence-cycle tag (Pre-arbitration / Arbitration) belongs on the
    // disputes list and the dispute page, not in a feed of what changed.
    render: (v) => (
      <Tooltip title={STATUS_DESCRIPTIONS[v] ? t(STATUS_DESCRIPTIONS[v]) : ""}>
        <span className="dispute-status-tag">
          {(STATUS_TAG_COLORS[v] || "default") === "default" ? (
            <Badge status="default" />
          ) : (
            <Badge color={STATUS_TAG_COLORS[v]} />
          )}
          <Text strong>{t(STATUS_LABELS[v] || v)}</Text>
        </span>
      </Tooltip>
    )
  },
  {
    key: "customer_name",
    dataIndex: "customer_name",
    title: t("Customer"),
    width: 260,
    render: (name, row) => {
      const txUrl = transactionUrl(row)
      return (
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
          {txUrl ? (
            <Tooltip title={name}>
              <Link
                href={txUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                ellipsis
              >
                <Private>{name || "."}</Private>
              </Link>
            </Tooltip>
          ) : (
            <Text ellipsis={{ tooltip: name }}>
              <Private>{name || "."}</Private>
            </Text>
          )}
          {row.customer_email ? (
            <span onClick={(e) => e.stopPropagation()} className="disputes-copy-cell">
              <Text
                type="secondary"
                ellipsis={{ tooltip: row.customer_email }}
                copyable={{
                  text: row.customer_email,
                  tooltips: [t("Copy"), t("Copied")],
                  icon: [<Icon key="c" type="copy" />, <Icon key="d" type="check_mark" />]
                }}
              >
                <Private>{row.customer_email}</Private>
              </Text>
            </span>
          ) : null}
        </Flex>
      )
    }
  },
  {
    key: "amount",
    dataIndex: "amount",
    title: t("Amount"),
    width: 120,
    render: (v) => <Private>{formatCurrency(Number(v) || 0)}</Private>
  },
  {
    key: "category",
    dataIndex: "category",
    // The cell carries both: the category on the first line, the network reason code under
    // it with its meaning on the icon. The header now says so.
    title: t("Category and reason code"),
    width: 220,
    render: (v, row) => (
      <Flex vertical gap={2}>
        <Text>{t(row.category_label || v)}</Text>
        {row.reason_code ? (
          <Flex align="center" gap={4}>
            <Text type="secondary" style={{ fontVariantNumeric: "tabular-nums" }}>
              {row.reason_code}
            </Text>
            {row.reason_code_label ? <InfoTooltip title={row.reason_code_label} /> : null}
          </Flex>
        ) : null}
      </Flex>
    )
  },
  {
    // What actually happened to this dispute. Without it a row shows a state but not the
    // event that produced it, which is the whole point of a feed.
    key: "change",
    dataIndex: "event_type",
    title: t("What changed"),
    width: 260,
    render: (type, row) => {
      if (!ACTIVITY_LABELS[type]) return <Text type="secondary">{t("N/A")}</Text>
      const label = t(ACTIVITY_LABELS[type], row.event_params || undefined)
      return (
        <Flex align="center" gap={4} style={{ minWidth: 0 }}>
          <Text ellipsis={{ tooltip: label }}>{label}</Text>
          {EVENT_DETAILS[type] ? <InfoTooltip title={t(EVENT_DETAILS[type])} /> : null}
        </Flex>
      )
    }
  },
  {
    key: "when",
    dataIndex: "event_time",
    title: t("When"),
    // Wide enough for a full "04 Sep 2026, 10:38 (UTC)" without wrapping.
    width: 210,
    align: "right",
    render: (v) => {
      const relative = isRelative(v)
      // A relative time is short and must never break; an exact one is now long enough
      // ("06 Sep 2026, 10:56 (UTC)") to outgrow this column, so it is allowed to wrap after
      // the date rather than spill over the neighbouring cell. The zone is bound to the clock
      // time with a non-breaking space, so the break never leaves "(UTC)" on its own line.
      const text = (
        <Text type="secondary" style={{ whiteSpace: relative ? "nowrap" : "normal" }}>
          {eventTimeText(v, t)}
        </Text>
      )
      return relative ? <Tooltip title={exactTime(v)}>{text}</Tooltip> : text
    }
  }
]
